import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { ProfilecardTriggerPosition } from '@atlaskit/profilecard/types';
import { TeamsAppAnalyticsContext } from '@atlaskit/teams-app-internal-analytics';
import { TeamsNavigationProvider } from '@atlaskit/teams-app-internal-navigation';

import { ReportingLinesSkeleton } from './ReportingLines/Skeleton';
import { ReportingLinesProvider } from './ReportingLinesContext';
import { ReportingLinesWithQuery } from './ReportingLinesWithQuery';

export { ReportingLinesSkeleton };

type ReportingLinesProps = {
	product?: string;
	userId: string;
	WrappedWithProfilecard?: React.ComponentType<{
		children: React.ReactElement;
		userId: string;
		position?: ProfilecardTriggerPosition;
	}>;
	/**
	 * Optional attributes to merge into the ReportingLinesChart.viewed event (e.g. tab, isNewUserProfile for user profile)
	 */
	viewedEventAttributes?: Record<string, string | boolean>;
	selectedCardRef?: React.RefObject<HTMLDivElement>;
};

/**
 * The Reporting Lines component is a resourced component that fetches the reporting lines for a given user and renders them in a chart.
 * @param product - The host product of the reporting lines, use for analytics.
 * @param userId - The ID of the user to get the reporting lines for the starting point of the chart.
 * @param WrappedWithProfilecard - A resourced component to wrap reporting lines in profile cards with actions relevant to the context.
 * @param viewedEventAttributes - Optional attributes to merge into the ReportingLinesChart.viewed event.
 */
const ReportingLines = ({
	product,
	userId,
	WrappedWithProfilecard,
	viewedEventAttributes,
	selectedCardRef,
}: ReportingLinesProps): React.JSX.Element => {
	/**
	 * This is the same code block as below but with analytics context.
	 */
	if (fg('ptc-enable-ptc-common-analytics-refactor')) {
		if (fg('ptc-links-migrate-atlaskit-link')) {
			return (
				<TeamsAppAnalyticsContext data={{ source: 'userProfileScreen' }}>
					<TeamsNavigationProvider
						value={{
							navigate: () => {},
						}}
					>
						<ReportingLinesProvider
							product={product}
							WrappedWithProfilecard={WrappedWithProfilecard}
							viewedEventAttributes={viewedEventAttributes}
							selectedCardRef={selectedCardRef}
						>
							<ReportingLinesWithQuery userId={userId} />
						</ReportingLinesProvider>
					</TeamsNavigationProvider>
				</TeamsAppAnalyticsContext>
			);
		}

		return (
			<TeamsAppAnalyticsContext data={{ source: 'userProfileScreen' }}>
				<ReportingLinesProvider
					product={product}
					WrappedWithProfilecard={WrappedWithProfilecard}
					viewedEventAttributes={viewedEventAttributes}
					selectedCardRef={selectedCardRef}
				>
					<ReportingLinesWithQuery userId={userId} />
				</ReportingLinesProvider>
			</TeamsAppAnalyticsContext>
		);
	}

	if (fg('ptc-links-migrate-atlaskit-link')) {
		return (
			<TeamsNavigationProvider
				value={{
					navigate: () => {},
				}}
			>
				<ReportingLinesProvider
					product={product}
					WrappedWithProfilecard={WrappedWithProfilecard}
					viewedEventAttributes={viewedEventAttributes}
					selectedCardRef={selectedCardRef}
				>
					<ReportingLinesWithQuery userId={userId} />
				</ReportingLinesProvider>
			</TeamsNavigationProvider>
		);
	}

	return (
		<ReportingLinesProvider
			product={product}
			WrappedWithProfilecard={WrappedWithProfilecard}
			viewedEventAttributes={viewedEventAttributes}
			selectedCardRef={selectedCardRef}
		>
			<ReportingLinesWithQuery userId={userId} />
		</ReportingLinesProvider>
	);
};

export default ReportingLines;
